import { useNavigate, useParams } from 'react-router';
import { useEffect, useState, useMemo } from 'react';
import { CustomInput } from '@components/CustomInput';
import {
  Button,
  Title,
  Alert,
  Loader,
  Center,
  Group,
  Container,
  Box,
  Flex,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import routes from '@commons/routes';
import { useUser } from '@contexts/UserContext';
import { useAppDispatch, useAppSelector } from '@store';
import {
  fetchClientFolder,
  updateClientFile,
} from '@store/slices/foldersSlice';
import {
  selectFolderByFolderName,
  selectFoldersLoading,
} from '@store/selectors/foldersSelectors';

export const FileEditPage = () => {
  const intl = useIntl();
  const { user } = useUser();
  let { folderName } = useParams();
  folderName = folderName?.toUpperCase();
  const { fileName } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectFoldersLoading);
  const folder = useAppSelector((state) =>
    selectFolderByFolderName(folderName || '')(state)
  );

  const file = useMemo(() => {
    if (!folder || !fileName) return null;

    return folder.files.find(
      (f: any) => f.id === fileName || f.name === fileName
    );
  }, [folder, fileName]);

  // Local state to track field changes without updating Redux
  const [localFields, setLocalFields] = useState<any[]>([]);

  // Initialize local fields when file loads
  useEffect(() => {
    if (file?.fields) {
      setLocalFields(file.fields);
    }
  }, [file?.id]); // Only depend on file ID to prevent loops

  useEffect(() => {
    if (!folderName) return;

    // Only fetch if folder doesn't exist in Redux (first render)
    if (!folder) {
      dispatch(fetchClientFolder({ folderName }))
        .unwrap()
        .catch((err: any) => {
          if (err.status === 401) {
            navigate(routes.routes.clientLogin(folderName));
          } else if (err.status === 403) {
            // Access denied - redirect to access page
            navigate(routes.routes.clientLogin(folderName as string));
          } else {
            notifications.show({
              title: intl.formatMessage({ id: 'error' }),
              message:
                err.message || intl.formatMessage({ id: 'failedToLoadFile' }),
              color: 'red',
              autoClose: 3000,
            });
          }
        });
    }
  }, [folderName, navigate, dispatch, folder]);

  const handleSubmit = async () => {
    if (!folder || !file) return;

    try {
      // Update Redux store with local field changes
      await dispatch(
        updateClientFile({
          folderId: folder.id,
          fileId: file.id,
          updates: {
            fields: localFields,
          },
        })
      ).unwrap();

      user
        ? navigate(-1)
        : navigate(routes.routes.clientFolder(folderName as string));

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'changesSaved' }),
        color: 'green',
        autoClose: 3000,
      });
    } catch (err: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: err.message || 'Failed to save changes',
        color: 'red',
        autoClose: 3000,
      });
    }
  };

  if (isLoading && !file) {
    return (
      <Center h={250}>
        <Loader size="lg" aria-label={intl.formatMessage({ id: 'loading' })} />
      </Center>
    );
  }

  if (!file || !folder) {
    return null;
  }

  return (
    <Container size="sm" my="md" mb={200}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Flex direction="column" gap="md">
          <Title order={3}>{file.name}</Title>

          {file.isVerified && (
            <Alert
              variant="light"
              color="yellow"
              title={intl.formatMessage({ id: 'attention' }) + '!'}
              icon={<IconAlertTriangle />}
            >
              {intl.formatMessage({
                id: 'reportChangesToTheRepresentative',
              })}
            </Alert>
          )}

          {!file.isVerified && (
            <Alert
              variant="light"
              color="blue"
              title={intl.formatMessage({ id: 'information' })}
              icon={<IconInfoCircle />}
            >
              {intl.formatMessage({
                id: 'pleaseConfirmChangesInTheDocumentMenu',
              })}
            </Alert>
          )}

          {localFields.map((field: any, index: number) => (
            <Box
              key={`${field.name}-${index}`}
              style={{
                border: [
                  'signature',
                  'divider',
                  'title',
                  'disclaimer',
                ].includes(field.type)
                  ? 'none'
                  : '1px solid var(--mantine-color-default-border)',
                padding: [
                  'signature',
                  'divider',
                  'title',
                  'disclaimer',
                ].includes(field.type)
                  ? '0'
                  : '10px',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
            >
              <CustomInput
                key={`${field.name}-${index}`}
                label={field.label}
                name={field.name}
                type={field.type}
                value={field.value}
                description={field.description || ''}
                items={field.items || []}
                title={field.title || ''}
                isDisabled={
                  user
                    ? false
                    : file.isLocked
                      ? true
                      : field.isDisabled && !user
                        ? true
                        : false
                }
                isReadonly={field.isReadonly || false}
                isRequired={field.isRequired || false}
                availableImages={field.availableImages}
                maxSelection={field.maxSelection}
                inputs={field.inputs || []}
                variant={field.variant}
                diacritics={field.diacritics || []}
                autoCorrect={field.autoCorrect || []}
                dateMinValue={field.dateMinValue}
                customInput={field.customInput}
                onChange={(value: any) => {
                  // Update local state only, not Redux store
                  setLocalFields((prevFields) => {
                    const newFields = [...prevFields];
                    newFields[index] = {
                      ...newFields[index],
                      value: value,
                    };
                    return newFields;
                  });
                }}
              />
            </Box>
          ))}
        </Flex>

        <Group justify="space-between" my="xl">
          <Button
            id="document-edit-back-button"
            variant="default"
            onClick={() => navigate(-1)}
          >
            {intl.formatMessage({ id: 'back' })}
          </Button>

          <Button
            id="document-edit-save-button"
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {intl.formatMessage({ id: 'saveChanges' })}
          </Button>
        </Group>
      </form>
    </Container>
  );
};
